import { zodResolver } from '@hookform/resolvers/zod';
import {
  IRevRecSettingsResponse,
  RevRecProrationModelEnum,
  RevRecSettingsResponse,
} from '@monetize/types/app';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  useRevRecSettings,
  useSaveRevRecSettings,
} from '../../../api/revenueService';
import {
  MFlex,
  MPageContainer,
  MPageLoader,
  MSettingsPageHeader,
} from '../../../components/Monetize';
import { useAsyncStatus } from '../../../hooks/useAsyncStatus';
import {
  getHighlightStyles,
  useSettingsSectionHighlight,
} from '../../../hooks/useSettingsSectionHighlight';
import { SettingsSection } from '../components/SettingsSection';
import { SettingsToggle } from '../components/SettingsToggle';

export const RevRecSettings = () => {
  const { data: revRecSettings, isLoading: isLoadingRevRecSettings } =
    useRevRecSettings();

  useEffect(() => {
    if (revRecSettings) {
      reset(revRecSettings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revRecSettings]);

  const { mutateAsync } = useSaveRevRecSettings();

  const { control, reset, getValues } = useForm<IRevRecSettingsResponse>({
    resolver: zodResolver(RevRecSettingsResponse),
    mode: 'onChange',
    defaultValues: revRecSettings || {
      enabled: false,
      prorationModel: RevRecProrationModelEnum.DAY_BASED,
    },
  });

  const onSubmit = () => mutateAsync(getValues());

  const { status, track } = useAsyncStatus();
  const isHighlighted = useSettingsSectionHighlight('enableRevenueRecognition');

  if (isLoadingRevRecSettings) {
    return <MPageLoader />;
  }

  return (
    <MPageContainer alignItems="stretch">
      <MSettingsPageHeader title="Revenue" />

      <MFlex maxW="600px" flexDirection="column">
        <SettingsSection
          id="enableRevenueRecognition"
          {...getHighlightStyles(isHighlighted)}
          showDivider={false}
        >
          <Controller
            name="enabled"
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <SettingsToggle
                formControlProps={{ pt: 0 }}
                name={name}
                heading="Enable Revenue Recognition"
                subheading="Enable or disable creation of revenue recognition schedules"
                value={value}
                asyncStatus={status}
                onChange={(checked) => {
                  onChange(checked);
                  track(onSubmit);
                }}
              />
            )}
          />
        </SettingsSection>
      </MFlex>
    </MPageContainer>
  );
};
